import React, { useRef, useEffect } from "react";
import PageWrapper from "@components/layouts/PageWrapper";
import { useLocation } from "react-router-dom";
import { useErrorHandling } from "@hooks/useErrorHandling";
import { castTypeArr } from "@utils/general/cast-type";
import { LoadingSpinner } from "@components/layouts/LoadingSpinner";
import { format } from "date-fns";
import * as XLSX from "xlsx";
import { useGetPendingDeliveryGoodIssuesQuery } from "@states/report/report.api";
import ReportFilter from "@components/filters/ReportFilter";
import CustomButton from "@components/buttons/CustomButton";
import { formatFilterDate } from "@utils/general/format-filter-date";

const PendingDeliveryGoodIssuePage = () => {
  const today = new Date();
  const lastMonth = new Date();
  lastMonth.setMonth(today.getMonth() - 1);
  const filterRef = useRef({
    from_date: undefined,
    to_date: undefined,
  });

  const location = useLocation();

  const {
    data,
    isFetching,
    error: getAllError,
    refetch,
  } = useGetPendingDeliveryGoodIssuesQuery({
    //@ts-ignore
    from_date: filterRef.current?.from_date || formatFilterDate(lastMonth),
    //@ts-ignore
    to_date: filterRef.current?.to_date || formatFilterDate(today),
  });

  //@ts-ignore
  // A custom hook that will handle fetch/mutation errors
  useErrorHandling(...castTypeArr([getAllError]));

  useEffect(() => {
    if (location.state?.reload) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.reload]);

  // @ts-ignore
  const total = data?.data?.reduce((sum, item) => sum + item.balance, 0);

  const handleExportClick = () => {
    // @ts-ignore
    if (!data?.data) return;

    const worksheetData = [
      [
        "Ref No",
        "Customer BU",
        "Qty",
        "Unit",
        "Way",
        "Allocation Account",
        "Delivery Target Date",
        "Issued Date",
        "Delivered Date",
        "Status",
        "Driver/Vehicle Note",
      ],
      // @ts-ignore
      ...data?.data?.map((item: any) => [
        item.reference_id,
        item.business_unit_name || "-",
        item.balance,
        item.unit_name,
        item.way_name || "-",
        item.warehouse_name,
        "-", // not provided in original data
        "-", // not provided in original data
        item.delivery_date ? format(item.delivery_date, "dd MMM yyyy") : "-",
        item.status,
        item.description || "-",
      ]),
      [
        "Totals:",
        "",
        // @ts-ignore
        data?.data?.reduce((sum, d: any) => sum + +d.balance, 0),
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
      ],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Pending Delivery Good Issue"
    );
    XLSX.writeFile(workbook, "pending-delivery-good-issue.xlsx");
  };

  return (
    <>
      {isFetching && <LoadingSpinner />}
      {/* <Search setSearch={setSearch} /> */}
      <PageWrapper title="Pending Delivery Good Issue">
        <div className="grid grid-cols-8 justify-evenly">
          <div className="col-span-6">
            {/* @ts-ignore */}
            <ReportFilter refetch={refetch} filterRef={filterRef} />
          </div>
          <div className=" flex items-center justify-end  mt-2 col-span-2">
            <CustomButton
              title={
                <div className="flex  gap-3">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.4126 17.2325V1H10.102C6.73567 1.60819 3.37848 2.17076 0 2.75462V18.1083C3.32374 18.6952 6.69006 19.2821 10.0351 19.8903H11.4035V17.2325H11.4126ZM6.46198 13.8632C6.04269 12.9666 5.67515 12.0467 5.36117 11.1081C5.05707 11.9991 4.63742 12.8384 4.2938 13.7111C3.81333 13.7111 3.33286 13.6837 2.8524 13.6655C3.41497 12.5616 3.9593 11.4486 4.54011 10.3539C4.04748 9.22268 3.50316 8.11579 2.99532 6.99368L4.44889 6.91158C4.77427 7.76912 5.1331 8.61754 5.40374 9.49637C5.70783 8.5628 6.12444 7.67181 6.49544 6.78386C6.99212 6.74939 7.48982 6.71898 7.98853 6.69263C7.40467 7.89278 6.81575 9.09598 6.22175 10.3022C6.82994 11.5186 7.43813 12.7502 8.04631 13.9787C7.51719 13.9361 6.99111 13.9027 6.46198 13.8632Z"
                      fill="white"
                    />
                    <path
                      d="M11.9658 3.10352V4.26819H14.2921V5.98632H11.9658V6.92901H14.2921V8.64714H11.9658V9.58983H14.2921V11.3049H11.9658V12.2476H14.2921V13.9505H11.9658V14.948H14.2921V16.6113H11.9658V17.776H20V3.10352H11.9658ZM18.2271 16.6235H14.8456V14.9601H18.2271V16.6235ZM18.2271 13.9627H14.8456V12.2446H18.2271V13.9627ZM18.2271 11.3019H14.8456V9.58679H18.2271V11.3019ZM18.2271 8.64106H14.8456V6.92597H18.2271V8.64106ZM18.2271 5.98024H14.8456V4.26819H18.2271V5.98024Z"
                      fill="white"
                    />
                  </svg>
                  Export Excel
                </div>
              }
              color="bg-[#1E6E42] h-3/6"
              handleClick={handleExportClick}
              className=""
            />
          </div>
        </div>
        <table className="table-auto border border-gray-300 w-full">
          <thead>
            <tr>
              {/* <th className="text-gray-400 text-sm pr-4 py-4">No</th> */}
              <th className="text-gray-400 text-sm pr-4">Ref No</th>
              <th className="text-gray-400 text-sm pr-4">Customer BU</th>
              <th className="text-gray-400 text-sm pr-4">Qty</th>
              <th className="text-gray-400 text-sm pr-4">Unit</th>
              <th className="text-gray-400 text-sm pr-4">Way</th>
              <th className="text-gray-400 text-sm pr-4">Allocation Account</th>
              <th className="text-gray-400 text-sm pr-4">
                Delivery Target Date
              </th>
              <th className="text-gray-400 text-sm pr-4">Issued Date</th>
              <th className="text-gray-400 text-sm pr-4">Delivered Date</th>
              <th className="text-gray-400 text-sm pr-4">Status</th>
              <th className="text-gray-400 text-sm pr-4">
                Driver/Vehicle Note
              </th>
            </tr>
          </thead>
          <tbody className="">
            {
              //@ts-ignore
              data?.data?.map((row: any, index: number) => (
                <tr key={index}>
                  <td className="border border-gray-300 px-4 py-2">
                    {row.reference_id || "-"}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {row.business_unit_name || "-"}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {row.balance || "-"}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {row.unit_name || "-"}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {row.way_name || "-"}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {row.warehouse_name || "-"}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {row.delivery_target_date
                      ? format(row.delivery_target_date, "dd MMM yyyy")
                      : "-"}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {row.issue_date
                      ? format(row.issue_date, "dd MMM yyyy")
                      : "-"}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {row.delivery_date
                      ? format(row.delivery_date, "dd MMM yyyy")
                      : "-"}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {row.status || "-"}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {row.description || "-"}
                  </td>
                </tr>
              ))
            }
            <tr className="font-bold">
              <td colSpan={2} className="border border-gray-300 px-4 py-2">
                Totals:
              </td>
              <td className="border border-gray-300 px-4 py-2">{total}</td>
            </tr>
          </tbody>
        </table>
      </PageWrapper>
    </>
  );
};

export default PendingDeliveryGoodIssuePage;
